import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { ArrowUpDown } from 'lucide-react';
import { SearchResultCard, type SearchResult } from './SearchResultCard';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@shared/components/ui/pagination';

interface SearchResultsProps {
  results: SearchResult[];
  totalResults: number;
  searchQuery: string;
  sortBy: string;
  onSortChange: (value: string) => void;
  onResultClick?: (result: SearchResult) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

export function SearchResults({
  results,
  totalResults,
  searchQuery,
  sortBy,
  onSortChange,
  onResultClick,
  currentPage = 1,
  totalPages = 95,
  onPageChange,
}: SearchResultsProps) {
  return (
    <div className="flex-1 space-y-4">
      {/* Results Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg text-muted-foreground font-medium">
          <span className="text-foreground font-bold text-xl">{totalResults.toLocaleString('nl-NL')}</span> resultaten gevonden
          {searchQuery && (
            <span className="text-base"> voor "<span className="text-foreground font-medium">{searchQuery}</span>"</span>
          )}
        </h2>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <span className="text-sm text-muted-foreground whitespace-nowrap hidden sm:inline">Sorteer op:</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-full sm:w-48 bg-card border-border rounded-lg shadow-sm">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Datum (nieuwste eerst)</SelectItem>
              <SelectItem value="date-asc">Datum (oudste eerst)</SelectItem>
              <SelectItem value="relevance">Relevantie</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results List */}
      <div className="space-y-4">
        {results.map(result => (
          <SearchResultCard
            key={result.id}
            result={result}
            onClick={() => onResultClick?.(result)}
            searchQuery={searchQuery}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange?.(currentPage - 1);
                  }}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {/* Generate pagination items */}
              {(() => {
                const pages = [];
                
                // Always show first page
                pages.push(
                  <PaginationItem key={1}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage !== 1) onPageChange?.(1);
                      }}
                      isActive={currentPage === 1}
                      className="cursor-pointer"
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                );
                
                // Show ellipsis if current page is far from start
                if (currentPage > 3) {
                  pages.push(
                    <PaginationItem key="ellipsis-start">
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                
                // Show page 2 if we're on page 2 or adjacent to it
                if (currentPage === 2 || (currentPage === 1 && totalPages >= 2) || currentPage === 3) {
                  pages.push(
                    <PaginationItem key={2}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage !== 2) onPageChange?.(2);
                        }}
                        isActive={currentPage === 2}
                        className="cursor-pointer"
                      >
                        2
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                
                // Show pages around current page (but not 1, 2, or last)
                for (let i = Math.max(3, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                  if (i === currentPage || (i === currentPage - 1 && currentPage > 3) || (i === currentPage + 1 && currentPage < totalPages - 1)) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage !== i) onPageChange?.(i);
                          }}
                          isActive={currentPage === i}
                          className="cursor-pointer"
                        >
                          {i}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                }
                
                // Show ellipsis if current page is far from end
                if (currentPage < totalPages - 2) {
                  pages.push(
                    <PaginationItem key="ellipsis-end">
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                
                // Always show last page if there's more than one page
                if (totalPages > 1) {
                  pages.push(
                    <PaginationItem key={totalPages}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage !== totalPages) onPageChange?.(totalPages);
                        }}
                        isActive={currentPage === totalPages}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                
                return pages;
              })()}
              
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange?.(currentPage + 1);
                  }}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}